import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { Send, Bot, User, Loader2, Search, FileText, CheckCircle, ExternalLink, ArrowRight, RefreshCw, Sparkles, X, Menu, Paperclip, Mic, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import API, { isTimeoutError } from "../lib/api";
import auditAgentAvatar from "../assets/audit-agent.svg";

export default function ChatPanel() {
  const navigate = useNavigate();
  const { auditRun, currentPhase, setCurrentPhase, triggerDataRefresh, refreshRun, advancePhase } = useAudit();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showIntroCard, setShowIntroCard] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const greetedRunRef = useRef(null);

  // Load chat history
  useEffect(() => {
    if (auditRun?.id) {
      setHistoryLoading(true);
      API.get(`/audit-runs/${auditRun.id}/chat`).then(({ data }) => {
        setMessages(data);
        // If there are existing messages, consider already greeted
        if (data.length > 0) {
          setHasGreeted(true);
          greetedRunRef.current = auditRun.id;
        }
      }).catch(() => {}).finally(() => setHistoryLoading(false));
    } else {
      setMessages([]);
      setHasGreeted(false);
      greetedRunRef.current = null;
      setHistoryLoading(false);
    }
  }, [auditRun?.id]);
  
  // Auto-resize textarea height
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = "inherit";
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(scrollHeight, 160)}px`;
    }
  }, [input]);



  // Pre-fill greeting when a new audit run is selected and no messages exist
  useEffect(() => {
    if (auditRun?.id && !historyLoading && messages.length === 0 && !hasGreeted && greetedRunRef.current !== auditRun.id) {
      setInput("Hello! I just started this audit. Please introduce yourself and guide me on what to do first.");
      greetedRunRef.current = auditRun.id;
    }
  }, [auditRun?.id, messages.length, hasGreeted, historyLoading]);

  useEffect(() => {
    if (!auditRun?.id) {
      setShowIntroCard(false);
      return;
    }
    const seenKey = `audit-agent-welcome-${auditRun.id}`;
    const hasSeen = localStorage.getItem(seenKey) === "1";
    setShowIntroCard(!hasSeen);
  }, [auditRun?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle action completion - refresh data and optionally navigate
  const handleActionComplete = (metadata) => {
    if (!metadata) return;
    
    const { action, count, navigate_to } = metadata;
    
    // Toast options with longer duration for action completions
    const toastOptions = { duration: 8000 }; // 8 seconds
    
    // Trigger data refresh based on action type
    if (action === 'ingest') {
      triggerDataRefresh('documents');
      toast.success(`${count} documents ingested`, {
        ...toastOptions,
        description: "View them in the Documents tab",
        action: {
          label: "Go to Documents",
          onClick: () => { setCurrentPhase(2); navigate('/phase/2'); }
        }
      });
    } else if (action === 'extract_controls') {
      triggerDataRefresh('controls');
      toast.success(`${count} controls extracted`, {
        ...toastOptions,
        description: "Review them in the Controls tab",
        action: {
          label: "Go to Controls",
          onClick: () => { setCurrentPhase(3); navigate('/phase/3'); }
        }
      });
    } else if (action === 'evaluate_evidence') {
      triggerDataRefresh('findings');
      toast.success(`${count} findings generated`, {
        ...toastOptions,
        description: "Review them in the Findings tab",
        action: {
          label: "Go to Findings",
          onClick: () => { setCurrentPhase(8); navigate('/phase/8'); }
        }
      });
    } else if (action === 'generate_report') {
      refreshRun();
      toast.success("Report generated", {
        ...toastOptions,
        description: "View it in the Reports tab",
        action: {
          label: "Go to Reports",
          onClick: () => { setCurrentPhase(10); navigate('/phase/10'); }
        }
      });
    }
    
    // Auto-navigate if specified
    if (navigate_to) {
      setCurrentPhase(navigate_to);
      navigate(`/phase/${navigate_to}`);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !auditRun?.id || sending) return;
    const msg = input.trim();
    setInput("");
    setSending(true);

    setMessages(prev => [...prev, { id: Date.now(), role: "user", content: msg, phase: currentPhase }]);

    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/chat`, { message: msg, phase: currentPhase });
      setMessages(prev => [...prev.slice(0, -1), data.user_message, data.assistant_message]);
      
      // Handle action completion
      if (data.assistant_message?.metadata?.type === 'action_complete') {
        handleActionComplete(data.assistant_message.metadata);
      }
    } catch (e) {
      const backendDetail =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error;
      const errorMessage = isTimeoutError(e)
        ? "Chat request timed out. The backend may be busy or the model call took too long."
        : backendDetail
          ? `Chat request failed: ${backendDetail}`
          : e?.message
            ? `Chat request failed: ${e.message}`
            : "Connection error. Please check your configuration and try again.";
      console.error("Chat request failed:", e);
      setMessages(prev => [...prev, {
        id: Date.now() + 1, role: "assistant",
        content: errorMessage,
        phase: currentPhase
      }]);
    }
    setSending(false);
    inputRef.current?.focus();
  };

  // Quick action buttons for chat suggestions
  const sendQuickMessage = (msg) => {
    setInput(msg);
    setTimeout(() => {
      const submitBtn = document.querySelector('[data-testid="chat-send-btn"]');
      if (submitBtn) submitBtn.click();
    }, 100);
  };

  const PHASE_NAMES = {
    1: "Context Setup", 2: "Document Ingestion", 3: "Control Mapping",
    4: "Audit Scope", 5: "Artifact Collection", 6: "Sampling Strategy",
    7: "Evidence Evaluation", 8: "Findings Draft", 9: "Classification Review",
    10: "Report Finalization"
  };
  const MAX_PHASE = 10;



  // Navigate to a specific phase
  const goToPhase = (phase) => {
    setCurrentPhase(phase);
    navigate(`/phase/${phase}`);
  };



  const handleContinueToNextPhase = async () => {
    if (!auditRun?.id || currentPhase >= MAX_PHASE) return;
    const nextPhase = currentPhase + 1;
    try {
      await advancePhase(nextPhase);
      navigate(`/phase/${nextPhase}`);
      toast.success(`Moved to Phase ${nextPhase}: ${PHASE_NAMES[nextPhase]}`);
    } catch (e) {
      toast.error("Failed to move to the next phase");
    }
  };

  const dismissIntroCard = () => {
    if (auditRun?.id) {
      localStorage.setItem(`audit-agent-welcome-${auditRun.id}`, "1");
    }
    setShowIntroCard(false);
  };

  const phaseProgress = useMemo(() => Math.round((Math.max(1, Math.min(MAX_PHASE, currentPhase)) / MAX_PHASE) * 100), [currentPhase]);
  const recentAssistant = useMemo(
    () => [...messages].reverse().find((m) => m.role === "assistant"),
    [messages]
  );
  const taskProgress = useMemo(() => {
    const action = recentAssistant?.metadata?.action;
    const base = {
      context: Math.max(20, phaseProgress - 10),
      controls: currentPhase >= 3 ? Math.max(35, phaseProgress) : 18,
      findings: currentPhase >= 8 ? Math.max(45, phaseProgress) : 12,
      reporting: currentPhase >= 10 ? 100 : Math.max(10, phaseProgress - 25),
    };
    if (action === "ingest") base.context = 100;
    if (action === "extract_controls") base.controls = 100;
    if (action === "evaluate_evidence") base.findings = 100;
    if (action === "generate_report") base.reporting = 100;
    return base;
  }, [recentAssistant?.metadata?.action, phaseProgress, currentPhase]);

  // Format message with basic markdown-like styling
  const formatMessage = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      let formatted = line.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');
      
      if (/^\d+\.\s/.test(line)) {
        const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
        if (urlMatch) {
          const url = urlMatch[1];
          formatted = formatted.replace(url, `<a href="${url}" target="_blank" rel="noopener" class="text-emerald-500 hover:underline text-xs break-all">${url}</a>`);
        }
      }
      
      return (
        <span key={idx}>
          <span dangerouslySetInnerHTML={{ __html: formatted }} />
          {idx < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  // Render action buttons based on metadata
  const renderActionButtons = (metadata) => {
    if (!metadata) return null;
    
    const buttons = [];
    
    if (metadata.type === 'action_complete') {
      const phaseMap = {
        'ingest': 2,
        'extract_controls': 3,
        'evaluate_evidence': 8,
        'generate_report': 10
      };
      const targetPhase = phaseMap[metadata.action];
      if (targetPhase) {
        buttons.push(
          <Button
            key="view"
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            onClick={() => goToPhase(targetPhase)}
          >
            View Results <ArrowRight className="w-3 h-3" />
          </Button>
        );
      }
    }
    
    if (metadata.type === 'search_results' && metadata.results?.length > 0) {
      buttons.push(
        <Button
          key="ingest-all"
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          onClick={() => sendQuickMessage("Yes, ingest all of them")}
        >
          Ingest All <ArrowRight className="w-3 h-3" />
        </Button>
      );
    }
    
    if (metadata.suggest_action) {
      buttons.push(
        <Button
          key="suggest"
          size="sm"
          className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700"
          onClick={() => sendQuickMessage(metadata.suggest_action)}
        >
          {metadata.suggest_label || "Do it"} <ArrowRight className="w-3 h-3" />
        </Button>
      );
    }
    
    return buttons.length > 0 ? (
      <div className="flex gap-2 mt-2 flex-wrap">{buttons}</div>
    ) : null;
  };

  return (
    <div className="audit-chat-panel copilot-shell h-screen border-l border-border bg-card flex flex-col" data-testid="chat-panel">
      {/* Header */}
      <div className="chat-header copilot-header shrink-0 border-b">
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="ghost"
            className="copilot-icon-btn h-9 w-9 p-0"
            title="Chat menu"
            aria-label="Chat menu"
          >
            <Menu className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="copilot-title truncate text-lg font-extrabold">AI Co-Pilot</p>
              {sending && <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-300" />}
            </div>
            <p className="copilot-context truncate text-[10px] font-mono uppercase">
              Active Context: {auditRun?.name || `Phase ${currentPhase}: ${PHASE_NAMES[currentPhase] || "Audit"}`}
            </p>
          </div>
          {auditRun && (
            <Button
              size="sm"
              variant="ghost"
              className="copilot-icon-btn h-9 w-9 p-0"
              onClick={() => triggerDataRefresh('all')}
              title="Refresh data"
              aria-label="Refresh data"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
          <img
            src={auditAgentAvatar}
            alt="Audit Agent"
            className="copilot-avatar h-10 w-10 rounded-full object-cover"
          />
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="copilot-scroll flex-1 p-4" ref={scrollRef}>
        <div className="space-y-5" data-testid="chat-messages">
          {auditRun && messages.length > 0 && (
            <div className="copilot-day-divider">
              <span>Today</span>
            </div>
          )}
          {auditRun && (
            <div className="assistant-overlay copilot-context-card metric-card-anim" style={{ "--delay": "50ms" }}>
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-900 dark:text-slate-100">
                  <ShieldCheck className="h-3.5 w-3.5 text-cyan-300" />
                  Audit Intelligence
                </p>
                <span className="text-[10px] text-blue-700 dark:text-blue-300">Phase {phaseProgress}%</span>
              </div>
              <div className="space-y-2 mt-2">
                {[
                  { label: "Context Intelligence", value: taskProgress.context },
                  { label: "Controls Coverage", value: taskProgress.controls },
                  { label: "Findings Validation", value: taskProgress.findings },
                  { label: "Final Report Readiness", value: taskProgress.reporting },
                ].map((item, idx) => (
                  <div key={item.label} className="assistant-task-row" style={{ "--task-delay": `${idx * 60}ms` }}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-slate-700 dark:text-slate-300">{item.label}</span>
                      <span className="text-[10px] font-semibold text-slate-900 dark:text-slate-100">{item.value}%</span>
                    </div>
                    <div className="assistant-task-track">
                      <div className="assistant-task-fill progress-anim" style={{ width: `${item.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {auditRun && showIntroCard && (
            <div className="copilot-welcome rounded-xl border border-teal-500/30 bg-teal-500/10 p-3 animate-in fade-in-0 slide-in-from-top-1 duration-300">
              <div className="flex items-start gap-3">
                <img
                  src={auditAgentAvatar}
                  alt="Audit Agent"
                  className="w-10 h-10 rounded-lg border border-teal-500/40"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">Welcome, I&apos;m Audit Agent</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    I can help configure context, find regulations, extract controls, and guide each phase.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                      onClick={() => {
                        sendQuickMessage("Help me get started with this audit step by step.");
                        dismissIntroCard();
                      }}
                    >
                      Start Guided Setup
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={dismissIntroCard}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
                <button
                  onClick={dismissIntroCard}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Close welcome card"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
          {!auditRun && (
            <div className="text-center text-sm text-muted-foreground py-8">
              Create or select an audit run to begin.
            </div>
          )}
          {auditRun && messages.length === 0 && !sending && (
            <div className="space-y-4 py-4">
              <div className="text-center text-sm text-muted-foreground">
                <img
                  src={auditAgentAvatar}
                  alt="Audit Agent"
                  className="w-14 h-14 mx-auto mb-2 rounded-xl border border-teal-500/30"
                />
                <p>Welcome. I&apos;m your Audit Agent. I can help you:</p>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => sendQuickMessage("Search for RBI KYC guidelines")}
                  className="quick-action w-full text-left text-xs p-2 rounded border border-border bg-card/70 hover:border-primary/40 hover:bg-accent transition-colors"
                >
                  Search for regulatory documents
                </button>
                <button
                  onClick={() => sendQuickMessage("Extract controls from my documents")}
                  className="quick-action w-full text-left text-xs p-2 rounded border border-border bg-card/70 hover:border-primary/40 hover:bg-accent transition-colors"
                >
                  Extract controls from documents
                </button>
                <button
                  onClick={() => sendQuickMessage("Evaluate evidence against controls")}
                  className="quick-action w-full text-left text-xs p-2 rounded border border-border bg-card/70 hover:border-primary/40 hover:bg-accent transition-colors"
                >
                  Evaluate evidence
                </button>
                <button
                  onClick={() => sendQuickMessage("What should I do next?")}
                  className="quick-action w-full text-left text-xs p-2 rounded border border-border bg-card/70 hover:border-primary/40 hover:bg-accent transition-colors"
                >
                  Guide me through the audit
                </button>
              </div>
            </div>
          )}
          {auditRun && messages.length === 0 && sending && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="relative">
                <Bot className="w-10 h-10 text-teal-500" />
                <Sparkles className="w-4 h-4 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <p className="text-sm text-muted-foreground">Audit Agent is preparing your briefing...</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div
              key={msg.id || i}
              className={`chat-message-enter copilot-message-row flex gap-2 ${msg.role === 'user' ? 'justify-end message-user-row' : 'message-assistant-row'}`}
              style={{ "--message-delay": `${Math.min(i, 8) * 70}ms` }}
            >
              {msg.role === 'assistant' && (
                <div className="shrink-0">
                  <div className="copilot-bot-mark flex h-8 w-8 items-center justify-center rounded-xl">
                    <Bot className="h-4 w-4" />
                  </div>
                </div>
              )}
              <div className={`copilot-message max-w-[86%] px-4 py-3 text-sm leading-relaxed
                ${msg.role === 'user'
                  ? 'copilot-user-message text-white'
                  : 'assistant-overlay-card copilot-assistant-message text-slate-900 dark:text-slate-100 border border-blue-200/70 dark:border-blue-400/15'}
              `}>
                {msg.role === 'assistant' && (
                  <div className="mb-2 text-xs font-semibold text-slate-500 dark:text-slate-300">
                    AI Co-Pilot
                  </div>
                )}
                {/* Render metadata (search results, action status) */}
                {msg.metadata?.type === 'search_results' && (
                  <div className="copilot-evidence-card mb-2 pb-2 border-b border-border/50">
                    <div className="mb-2 flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-300">
                      <Search className="w-3 h-3" />
                      <span>Search: "{msg.metadata.query}"</span>
                      {msg.metadata.source === 'curated' && (
                        <span className="rounded bg-blue-500/20 px-1 text-[10px]">curated</span>
                      )}
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {msg.metadata.results?.slice(0, 5).map((r, idx) => (
                        <a 
                          key={idx}
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-1.5 rounded bg-background/50 hover:bg-background transition-colors group"
                        >
                          <div className="flex items-start gap-1.5">
                            <FileText className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors">
                                {idx + 1}. {r.title}
                              </p>
                              <p className="text-[10px] text-muted-foreground line-clamp-2">{r.snippet?.slice(0, 80)}...</p>
                            </div>
                            <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {msg.metadata?.type === 'action_complete' && (
                  <div className="mb-2 pb-2 border-b border-border/50">
                    <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-300">
                      <CheckCircle className="w-3 h-3" />
                      <span className="font-medium">
                        {msg.metadata.action === 'ingest' && `${msg.metadata.count} documents ingested`}
                        {msg.metadata.action === 'extract_controls' && `${msg.metadata.count} controls extracted`}
                        {msg.metadata.action === 'evaluate_evidence' && `${msg.metadata.count} findings generated`}
                        {msg.metadata.action === 'generate_report' && 'Report generated'}
                      </span>
                    </div>
                  </div>
                )}
                {/* Format message content with markdown-like styling */}
                <div className="copilot-message-text whitespace-pre-wrap">
                  {formatMessage(msg.content)}
                </div>
                {/* Render action buttons */}
                {msg.role === 'assistant' && renderActionButtons(msg.metadata)}
              </div>
              {msg.role === 'user' && (
                <div className="copilot-user-mark w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <User className="w-3.5 h-3.5" />
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="copilot-thinking-row flex gap-2 items-center text-muted-foreground">
              <div className="copilot-bot-mark copilot-thinking-mark flex h-8 w-8 items-center justify-center rounded-xl">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              </div>
              <div className="copilot-thinking-bubble rounded-2xl border px-4 py-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800 dark:text-slate-100">Agent is scanning control narratives</span>
                  <span className="copilot-typing-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="chat-composer p-3 border-t shrink-0">
        <div className="mb-2 flex gap-2">

          <Button
            size="sm"
            variant="outline"
            onClick={handleContinueToNextPhase}
            disabled={!auditRun || sending || currentPhase >= MAX_PHASE}
            className="copilot-chip h-8 shrink-0 text-xs gap-1"
            data-testid="chat-continue-btn"
          >
            Continue Phase <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
        <div className="flex gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={auditRun ? "Ask the audit agent..." : "Select an audit run first"}
            disabled={!auditRun || sending}
            className="min-h-[38px] max-h-40 bg-card/80 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-ring resize-none py-2"
            data-testid="chat-input"
            rows={1}
          />
          <Button
            size="sm"
            variant="ghost"
            className="copilot-input-icon h-9 w-9 shrink-0 p-0"
            disabled={!auditRun || sending}
            title="Attach evidence"
            aria-label="Attach evidence"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!auditRun || !input.trim() || sending}
            className={`copilot-send h-11 w-11 shrink-0 p-0 text-white ${input.trim() ? "copilot-send-armed" : ""}`}
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
